/**
 * Pixi Application Wrapper
 * 
 * Phase 6.0 Track C: Core Pixi.js application management
 */

import { Application } from "pixi.js";

export interface PixiAppOptions {
  width: number;
  height: number;
  backgroundColor?: number;
  antialias?: boolean;
  resolution?: number;
}

/**
 * Pixi Application Manager
 * 
 * Handles Pixi app lifecycle, canvas management, and resize
 */
export class PixiApp {
  private app: Application | null = null;
  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  /**
   * Initialize Pixi application
   */
  async init(container: HTMLElement, options: PixiAppOptions): Promise<void> {
    this.container = container;

    // Create Pixi app
    this.app = new Application();

    await this.app.init({
      width: options.width,
      height: options.height,
      backgroundColor: options.backgroundColor || 0x1a1a2e,
      antialias: options.antialias !== false,
      resolution: options.resolution || window.devicePixelRatio || 1,
      autoDensity: true,
    });

    // Append canvas to container
    container.appendChild(this.app.canvas);

    // Setup resize handling
    this.setupResize();
  }

  /**
   * Setup responsive resize
   */
  private setupResize(): void {
    if (!this.container || !this.app) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.resize(width, height);
      }
    });

    this.resizeObserver.observe(this.container);
  }

  /**
   * Resize canvas
   */
  resize(width: number, height: number): void {
    if (!this.app) return;

    this.app.renderer.resize(width, height);
  }

  /**
   * Get Pixi app instance
   */
  getApp(): Application | null {
    return this.app;
  }

  /**
   * Get stage
   */
  getStage() {
    return this.app?.stage;
  }

  /**
   * Destroy app and cleanup
   */
  destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.app) {
      this.app.destroy(true, { children: true, texture: true });
      this.app = null;
    }

    this.container = null;
  }
}
